import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, LogOut, Search, Plus, Menu, X, Shield, User as UserIcon, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TownEyeLogo } from "@/components/TownEyeLogo";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [query, setQuery] = useState("");
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        setUnread(data.count || 0);
      } catch (e) { /* noop */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [user, loc.pathname]);

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const navItems = [
    { to: "/dashboard", label: "Feed" },
    { to: "/map", label: "Map" },
    { to: "/community", label: "Community" },
    { to: "/notifications", label: "Notifications" },
  ];
  if (user?.role === "admin") navItems.push({ to: "/admin", label: "Admin" });

  return (
    <header className="te-glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
        <Link to="/dashboard" data-testid="header-logo-link" className="flex-shrink-0">
          <TownEyeLogo size={26} />
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navItems.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname.startsWith(n.to)
                  ? "bg-[var(--te-input-bg)] text-[var(--te-text)]"
                  : "text-[var(--te-text-secondary)] hover:bg-[var(--te-input-bg)]/60"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md mx-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--te-text-muted)]" />
          <input
            data-testid="header-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reports, posts, people…"
            className="te-input pl-9 text-sm"
          />
        </form>

        <div className="flex items-center gap-2 ml-auto">
          <Link
            to="/report/new"
            data-testid="header-create-report-btn"
            className="hidden sm:inline-flex items-center gap-2 te-btn-accent text-sm py-2 px-4"
          >
            <Plus size={16} /> Report
          </Link>

          <Link
            to="/notifications"
            data-testid="header-notifications-link"
            className="relative p-2 rounded-full hover:bg-[var(--te-input-bg)] transition-colors"
          >
            <Bell size={20} className="text-[var(--te-text)]" />
            {unread > 0 && (
              <span
                data-testid="unread-badge"
                className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="header-user-menu-btn"
                className="flex items-center gap-2 p-1 pr-3 rounded-full hover:bg-[var(--te-input-bg)] transition-colors"
              >
                <Avatar user={user} size={32} />
                <span className="hidden md:block text-sm font-medium text-[var(--te-text)]">
                  {user?.name?.split(" ")[0]}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col">
                  <span className="font-medium">{user?.name}</span>
                  <span className="text-xs text-[var(--te-text-muted)]">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={`/profile/${user?.id}`} data-testid="menu-profile-link" className="cursor-pointer">
                  <UserIcon size={16} className="mr-2" /> My Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" data-testid="menu-settings-link" className="cursor-pointer">
                  <SettingsIcon size={16} className="mr-2" /> Settings
                </Link>
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem asChild>
                  <Link to="/admin" data-testid="menu-admin-link" className="cursor-pointer">
                    <Shield size={16} className="mr-2" /> Admin Panel
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
                className="cursor-pointer text-[#DC2626] focus:text-[#DC2626]"
              >
                <LogOut size={16} className="mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 rounded-full hover:bg-[var(--te-input-bg)]"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[var(--te-divider)] bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            <form onSubmit={onSearch} className="relative mb-2">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--te-text-muted)]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="te-input pl-9 text-sm"
                data-testid="mobile-search-input"
              />
            </form>
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="px-3 py-2.5 rounded-lg text-sm font-medium text-[var(--te-text)] hover:bg-[var(--te-input-bg)]"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/report/new"
              onClick={() => setMobileOpen(false)}
              className="te-btn-accent text-sm text-center mt-2 inline-flex items-center justify-center gap-2"
              data-testid="mobile-create-report-btn"
            >
              <Plus size={16} /> New Report
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function Avatar({ user, size = 36 }) {
  const initials = user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";
  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={user.name}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center font-semibold text-white"
      style={{ width: size, height: size, background: "var(--te-primary)", fontSize: size * 0.4 }}
    >
      {initials}
    </div>
  );
}
